import { useState } from 'react'
import { View, Text, Pressable, Modal, ScrollView, StyleSheet } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useTranslation } from 'react-i18next'
import Ionicons from '@expo/vector-icons/Ionicons'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import { fonts } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { useAvatars } from '../../lib/useAvatars'
import PersonAvatarFace from '../PersonAvatarFace'

// Selettore di un membro della lega: pulsante con faccia e nome, la scelta si fa in un foglio dal basso
// (come su VB), non in una tendina dentro lo scorrimento della pagina.
// members: righe di fn_classifica_stagionale (out_id_user, out_display_name).
export default function MemberPicker({ members, value, onChange, exclude, placeholder, title, style }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const insets = useSafeAreaInsets()
  const [open, setOpen] = useState(false)
  const avatars = useAvatars(members.map(m => m.out_id_user))

  const current = members.find(m => m.out_id_user === value)
  const list = [...members]
    .filter(m => m.out_id_user !== exclude)
    .sort((a, b) => a.out_display_name.localeCompare(b.out_display_name))

  return (
    <>
      <Pressable
        onPress={() => setOpen(true)}
        accessibilityRole="button"
        accessibilityLabel={`${title ?? t('leagues.pick_member')}: ${current?.out_display_name ?? placeholder}`}
        style={({ pressed }) => [s.btn, { backgroundColor: tc.bgCard2, borderColor: tc.border }, pressed && { opacity: 0.75 }, style]}
      >
        {current && <PersonAvatarFace userId={current.out_id_user} settings={avatars[current.out_id_user]} size={26} />}
        <Text style={[s.btnText, { color: current ? tc.text : tc.textDim }]} numberOfLines={1}>
          {current?.out_display_name ?? placeholder}
        </Text>
        <Ionicons name="chevron-down" size={14} color={tc.textMuted} />
      </Pressable>

      <Modal visible={open} transparent animationType="slide" onRequestClose={() => setOpen(false)}>
        <View style={s.overlay}>
          <Pressable style={StyleSheet.absoluteFill} onPress={() => setOpen(false)} accessibilityRole="button" accessibilityLabel={t('pick.close')} />
          <View style={[s.sheet, { backgroundColor: tc.bgCard, paddingBottom: insets.bottom + 12 }]}>
            <View style={[s.sheetHeader, { borderBottomColor: tc.border }]}>
              <Text style={[s.sheetTitle, { color: tc.text }]}>{title ?? t('leagues.pick_member')}</Text>
              <Pressable onPress={() => setOpen(false)} hitSlop={6} style={[s.closeBtn, { backgroundColor: tc.bgCard2 }]} accessibilityRole="button" accessibilityLabel={t('pick.close')}>
                <MaterialCommunityIcons name="close" size={18} color={tc.text} />
              </Pressable>
            </View>
            <ScrollView>
              {list.map(m => {
                const on = m.out_id_user === value
                return (
                  <Pressable
                    key={m.out_id_user}
                    onPress={() => { onChange(m.out_id_user); setOpen(false) }}
                    accessibilityRole="button"
                    accessibilityState={{ selected: on }}
                    style={({ pressed }) => [s.row, { borderBottomColor: tc.border }, on && { backgroundColor: tc.rosaSubtle }, pressed && { opacity: 0.7 }]}
                  >
                    <PersonAvatarFace userId={m.out_id_user} settings={avatars[m.out_id_user]} size={36} />
                    <Text style={[s.rowText, { color: tc.text, fontWeight: on ? '700' : '500' }]} numberOfLines={1}>{m.out_display_name}</Text>
                    {on && <MaterialCommunityIcons name="check" size={18} color={tc.rosaText} />}
                  </Pressable>
                )
              })}
            </ScrollView>
          </View>
        </View>
      </Modal>
    </>
  )
}

const s = StyleSheet.create({
  btn: { flexDirection: 'row', alignItems: 'center', gap: 8, minHeight: 44, borderWidth: 1, borderRadius: 10, paddingHorizontal: 10 },
  btnText: { flex: 1, fontSize: 13, fontFamily: fonts.condensedBold },
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'flex-end' },
  sheet: { borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: '75%' },
  sheetHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 0.5 },
  sheetTitle: { fontFamily: fonts.condensedBold, fontSize: 18 },
  closeBtn: { width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center' },
  row: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, paddingVertical: 8, minHeight: 56, borderBottomWidth: 0.5 },
  rowText: { flex: 1, fontSize: 14 },
})
